import React, { useState, useEffect, useRef } from 'react';
import LargePreviewModal from '../../components/ui/LargePreviewModal';

export default function GuestPhotoUpload({ eventId, eventName, db, storage }) {
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploaded, setUploaded] = useState(false);
    const [guestName, setGuestName] = useState('');
    const [gallery, setGallery] = useState([]);
    
    // Drag & Drop and Lightbox states
    const [isDragging, setIsDragging] = useState(false);
    const [selectedPhoto, setSelectedPhoto] = useState(null);
    const fileInputRef = useRef(null);
    const [photoTab, setPhotoTab] = useState('upload');

    // Fetch existing photos so guests and hosts can easily find and view the photo drop!
    useEffect(() => {
        let unsubscribe;
        const loadGallery = async () => {
            try {
                const { collection, onSnapshot } = await import('firebase/firestore');
                unsubscribe = onSnapshot(collection(db, `events/${eventId}/guest_photos`), (snapshot) => {
                    const fetched = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
                    // Sort in memory to comply with strict query rules
                    fetched.sort((a, b) => (b.uploadedAt?.toMillis() || 0) - (a.uploadedAt?.toMillis() || 0));
                    setGallery(fetched);
                });
            } catch (err) {
                console.error("Error loading gallery:", err);
            }
        };
        loadGallery();
        return () => unsubscribe && unsubscribe();
    }, [eventId, db]);

    const handleUpload = async () => {
        if (!files.length) return;
        setUploading(true);
        try {
            const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
            const { collection, addDoc, serverTimestamp } = await import('firebase/firestore');

            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const storageRef = ref(storage, `events/${eventId}/guest_photos/${Date.now()}_${file.name}`);
                await uploadBytes(storageRef, file);
                const url = await getDownloadURL(storageRef);
                
                await addDoc(collection(db, `events/${eventId}/guest_photos`), {
                    url,
                    uploadedAt: serverTimestamp(),
                    guestName: guestName || 'Anonymous Guest'
                });
            }
            setUploaded(true);
            setFiles([]);
            setGuestName('');
        } catch (err) {
            console.error("Upload failed", err);
            alert("Upload failed. Please try again.");
        }
        setUploading(false);
    };

    // Drag and Drop Handlers
    const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
    const handleDragLeave = (e) => { e.preventDefault(); setIsDragging(false); };
    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            setFiles(Array.from(e.dataTransfer.files));
        }
    };

    if (uploaded) {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 p-6 font-sans">
                <div className="bg-white p-10 rounded-[2rem] shadow-2xl text-center max-w-md w-full border border-pink-100 animate-fade-in-up">
                    <div className="w-20 h-20 bg-green-100 text-green-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" /></svg>
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-3 font-serif">Thank You!</h2>
                    <p className="text-gray-600 mb-8 text-lg">Your memories have been added to the event album.</p>
                    <div className="space-y-3">
                        <button onClick={() => { setUploaded(false); setPhotoTab('gallery'); }} className="w-full bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold py-4 rounded-xl transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5">
                            View Gallery
                        </button>
                        <button onClick={() => setUploaded(false)} className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold py-4 rounded-xl transition-all shadow-sm">
                            Upload More Photos
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-center min-h-screen bg-gray-50 font-sans pb-20">
            {selectedPhoto && <LargePreviewModal imageUrl={selectedPhoto} onClose={() => setSelectedPhoto(null)} />}
            
            {/* Premium Header */}
            <div className="w-full bg-white border-b border-gray-200 pt-12 pb-24 px-6 text-center relative overflow-hidden shadow-sm">
                <div className="absolute inset-0 bg-gradient-to-r from-pink-500/10 to-purple-500/10 opacity-50"></div>
                <div className="relative z-10 max-w-2xl mx-auto">
                    <span className="bg-pink-100 text-pink-700 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-widest shadow-sm">Photo Drop</span>
                    <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mt-4 mb-4 font-serif tracking-tight">
                        {eventName ? `Share Memories from ${eventName}` : 'Share Your Memories'}
                    </h1>
                    <p className="text-lg text-gray-600">Add your favorite shots to the shared event gallery for everyone to enjoy!</p>
                    
                    <div className="flex bg-white/80 backdrop-blur-md rounded-lg p-1 w-full sm:w-auto shadow-sm border border-pink-100 mx-auto mt-8 max-w-sm">
                        <button 
                            type="button"
                            onClick={() => setPhotoTab('upload')} 
                            className={`flex-1 sm:flex-none px-6 py-2 text-sm font-bold rounded-md transition-all ${photoTab === 'upload' ? 'bg-white shadow-sm text-pink-600 border border-pink-200' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            Upload Photos
                        </button>
                        <button 
                            type="button"
                            onClick={() => setPhotoTab('gallery')} 
                            className={`flex-1 sm:flex-none px-6 py-2 text-sm font-bold rounded-md transition-all ${photoTab === 'gallery' ? 'bg-white shadow-sm text-pink-600 border border-pink-200' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            View Gallery ({gallery.length})
                        </button>
                    </div>
                </div>
            </div>

            {/* Upload Card */}
            {photoTab === 'upload' && (
            <div className="bg-white p-8 md:p-10 rounded-[2rem] shadow-2xl max-w-xl w-full border border-gray-100 -mt-12 relative z-20 mb-16 mx-4 animate-fade-in">
                <div className="space-y-6">
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2 ml-1">Who's sharing? (Optional)</label>
                        <input 
                            type="text" 
                            value={guestName}
                            onChange={(e) => setGuestName(e.target.value)}
                            placeholder="e.g. Jane Doe" 
                            className="w-full px-4 py-3.5 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-pink-500 focus:outline-none transition-all font-medium text-gray-900"
                        />
                    </div>
                    
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2 ml-1">Select Photos</label>
                        <div 
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                            className={`w-full flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-2xl cursor-pointer transition-all duration-200 ${
                                isDragging 
                                ? 'border-pink-500 bg-pink-50 scale-[1.02]' 
                                : 'border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-pink-400'
                            }`}
                        >
                            <input 
                                type="file" 
                                multiple 
                                accept="image/*" 
                                ref={fileInputRef}
                                onChange={(e) => setFiles(Array.from(e.target.files))} 
                                className="hidden" 
                            />
                            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm mb-4 border border-gray-100 text-3xl">
                                📸
                            </div>
                            <p className="text-gray-900 font-bold mb-1">Click to upload or drag and drop</p>
                            <p className="text-sm text-gray-500">SVG, PNG, JPG or GIF (max. 10MB)</p>
                        </div>

                        {files && files.length > 0 && (
                            <div className="mt-4 flex flex-wrap gap-3 max-h-40 overflow-y-auto p-1 custom-scrollbar">
                                {Array.from(files).map((file, index) => (
                                    <div key={index} className="w-20 h-20 relative rounded-xl overflow-hidden shadow-sm border border-gray-200 flex-shrink-0 group">
                                        <img src={URL.createObjectURL(file)} alt={`Preview ${index}`} className="w-full h-full object-cover" />
                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-sm">
                                            <button 
                                                onClick={(e) => { e.stopPropagation(); setFiles(files.filter((_, i) => i !== index)); }}
                                                className="text-white bg-red-500 rounded-full p-1.5 hover:bg-red-600 hover:scale-110 transition-transform shadow-md"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <button 
                        onClick={handleUpload} 
                        disabled={uploading || !files.length} 
                        className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-bold py-4 rounded-xl disabled:opacity-50 disabled:transform-none transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center justify-center gap-2 text-lg mt-2"
                    >
                        {uploading ? (
                            <><span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></span> Uploading...</>
                        ) : (
                            <>Upload {files.length > 0 ? `${files.length} Photos` : ''}</>
                        )}
                    </button>
                </div>
            </div>
            )}

            {/* Premium Masonry Gallery */}
            {photoTab === 'gallery' && (
                <div className="max-w-6xl w-full px-4 md:px-8 relative z-20 -mt-12 mb-16 animate-fade-in">
                    {gallery.length > 0 ? (
                        <>
                            <div className="flex flex-col sm:flex-row items-center justify-between mb-8 gap-4 bg-white p-6 rounded-2xl shadow-xl border border-gray-100">
                                <h3 className="text-3xl font-bold text-gray-900 font-serif ml-2">Event Gallery</h3>
                                <span className="bg-gray-100 border border-gray-200 text-gray-700 text-sm font-bold px-5 py-2 rounded-full shadow-sm">
                                    {gallery.length} Photos
                                </span>
                            </div>
                            
                            <div className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4 pb-12">
                                {gallery.map(photo => (
                                <div key={photo.id} className="break-inside-avoid relative group rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 cursor-zoom-in border border-gray-100 bg-gray-200">
                                    <img src={photo.url} alt="Guest Upload" className="w-full h-auto object-cover transform group-hover:scale-[1.03] transition-transform duration-700" loading="lazy" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/0 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                                        <p className="text-white text-sm font-bold flex items-center gap-2 drop-shadow-md">
                                            <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-md text-xs shadow-inner">📸</span>
                                            {photo.guestName}
                                        </p>
                                    </div>
                                </div>
                        ))}
                    </div>
                    </>
                ) : (
                    <div className="bg-white p-10 rounded-[2rem] shadow-2xl max-w-xl mx-auto border border-gray-100 text-center">
                        <span className="text-5xl block mb-4">✨</span>
                        <h3 className="text-2xl font-bold text-gray-900 mb-2 font-serif">No photos yet</h3>
                        <p className="text-gray-600 mb-6">Be the first to share a memory from this event!</p>
                        <button onClick={() => setPhotoTab('upload')} className="bg-pink-50 text-pink-600 px-6 py-3 rounded-xl font-bold hover:bg-pink-100 transition">Go to Upload</button>
                    </div>
                )}
                </div>
            )}
        </div>
    );
}